import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MetricCard from './components/MetricCard';
import OrdersPerDealershipChart from './components/OrdersPerDealershipChart';
import MonthlyRevenueChart from './components/MonthlyRevenueChart';
import FilterPanel from './components/FilterPanel';
import TopPerformersCard from './components/TopPerformersCard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { orderService } from '../../services/orderService';
import { carHouseService } from '../../services/carHouseService';

const RevenueAnalyticsDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [filters, setFilters] = useState({
    selectedMonth: (new Date().getMonth() + 1).toString(), // Current month (1-12)
    selectedYear: new Date().getFullYear().toString(), // Current year
    selectedDealership: 'all',
    priceType: 'gross'
  });

  // Real data states
  const [analyticsData, setAnalyticsData] = useState({
    metrics: [],
    dealershipData: [],
    totalRevenueData: [],
    topDealerships: [],
    topTechnicians: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentYear = new Date().getFullYear();

  // Load real analytics data
  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const selectedMonth = parseInt(filters.selectedMonth);
      const selectedYear = parseInt(filters.selectedYear);

             // Load orders for the selected month (for metrics and dealership chart)
       const { data: ordersData, error: ordersError } = await orderService.getAllOrders({
         date_from: new Date(selectedYear, selectedMonth - 1, 1).toISOString(), // First day of month
         date_to: new Date(selectedYear, selectedMonth, 0, 23, 59, 59, 999).toISOString() // Last day of month
       });

       if (ordersError) throw ordersError;

       if (ordersData && ordersData.length > 0) {
       }

       // Load all dealerships
       const { data: dealershipsData, error: dealershipsError } = await carHouseService.getAllCarHouses();
       if (dealershipsError) throw dealershipsError;

       // Filter orders by dealership if specific one is selected
       let filteredOrders = ordersData || [];
       if (filters.selectedDealership !== 'all') {
         filteredOrders = filteredOrders.filter(order => 
           order.car_house_id === filters.selectedDealership
         );
       }

       // Filter only completed orders for metrics
       const completedOrders = filteredOrders.filter(order => 
         order.status === 'completed'
       );

       // Calculate metrics using only completed orders
       const totalRevenue = completedOrders.reduce((sum, order) => 
         sum + (parseFloat(order.total_amount) || 0), 0
       );
       
       const avgOrderValue = completedOrders.length > 0 
         ? totalRevenue / completedOrders.length 
         : 0;

       const activeDealerships = new Set(
         completedOrders.map(order => order.car_house_id)
       ).size;

       // Calculate revenue by dealership (using only completed orders)
       const dealershipRevenue = {};
       completedOrders.forEach(order => {
         const dealershipId = order.car_house_id;
         const dealershipName = order.car_house?.name || 'Unbekannt';
         
         if (!dealershipRevenue[dealershipId]) {
           dealershipRevenue[dealershipId] = {
             name: dealershipName,
             revenue: 0,
             orders: 0
           };
         }
         
         dealershipRevenue[dealershipId].revenue += parseFloat(order.total_amount) || 0;
         dealershipRevenue[dealershipId].orders += 1;
       });

       // Ensure all dealerships are shown, even with 0 orders
       const dealershipData = dealershipsData?.map(dealership => {
         const existingData = dealershipRevenue[dealership.id];
         return existingData || {
           name: dealership.name,
           revenue: 0,
           orders: 0
         };
       }).sort((a, b) => b.revenue - a.revenue).slice(0, 8) || [];

       // Load orders for the entire year (for monthly revenue chart)
       const { data: yearlyOrdersData, error: yearlyOrdersError } = await orderService.getAllOrders({
         date_from: new Date(selectedYear, 0, 1).toISOString(), // First day of year
         date_to: new Date(selectedYear, 11, 31, 23, 59, 59, 999).toISOString() // Last day of year
       });

       if (yearlyOrdersError) {
         console.error('Error fetching yearly orders:', yearlyOrdersError);
       }


       // Filter only completed orders for monthly revenue chart
       const completedYearlyOrders = (yearlyOrdersData || []).filter(order => 
         order.status === 'completed'
       );


       // Calculate monthly revenue for the selected year (for the monthly revenue chart)
       const monthlyRevenue = {};
       
       // Initialize all months with 0
       for (let month = 1; month <= 12; month++) {
         monthlyRevenue[month] = 0;
       }
       
       // Calculate revenue for each month of the selected year using only completed orders
       completedYearlyOrders.forEach(order => {
         const orderDate = new Date(order.created_at);
         if (orderDate.getFullYear() === selectedYear) {
           const month = orderDate.getMonth() + 1; // getMonth() returns 0-11
           monthlyRevenue[month] += parseFloat(order.total_amount) || 0;
         }
       });

       // Transform monthly revenue data for the chart
       const monthlyRevenueData = Object.entries(monthlyRevenue).map(([month, revenue]) => ({
         month: parseInt(month),
         revenue: revenue
       })).sort((a, b) => a.month - b.month);

      // Top performers
      const topDealerships = dealershipData.slice(0, 3).map((dealer, index) => ({
        id: index + 1,
        name: dealer.name,
        revenue: dealer.revenue,
        orders: dealer.orders,
        growth: Math.random() * 20 + 5 // Mock growth for now
      }));

             // Top technicians (based on completed orders)
       const technicianRevenue = {};
       completedOrders.forEach(order => {
         if (order.assigned_technician_id) {
           const techId = order.assigned_technician_id;
           const techName = order.assigned_technician?.full_name || 'Unbekannter Techniker';
           
           if (!technicianRevenue[techId]) {
             technicianRevenue[techId] = {
               name: techName,
               revenue: 0,
               completedOrders: 0
             };
           }
           
           technicianRevenue[techId].revenue += parseFloat(order.total_amount) || 0;
           technicianRevenue[techId].completedOrders += 1;
         }
       });

      const topTechnicians = Object.values(technicianRevenue)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 3)
        .map((tech, index) => ({
          id: index + 1,
          name: tech.name,
          revenue: tech.revenue,
          completedOrders: tech.completedOrders,
          efficiency: Math.floor(Math.random() * 15) + 85 // Mock efficiency for now
        }));

      // Create metrics array
      const metrics = [
    {
      title: 'Gesamtumsatz',
          value: `€ ${totalRevenue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          change: '+12,5%', // Mock change for now
      changeType: 'positive',
      icon: 'TrendingUp',
      color: 'primary'
    },
    {
      title: 'Durchschnittlicher Auftragswert',
          value: `€ ${avgOrderValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          change: '+8,2%', // Mock change for now
      changeType: 'positive',
      icon: 'Calculator',
      color: 'success'
    },
    {
      title: 'Aktive Autohäuser',
          value: activeDealerships.toString(),
          change: '+2', // Mock change for now
      changeType: 'positive',
      icon: 'Building2',
      color: 'accent'
    },
    {
      title: 'Abgeschlossene Aufträge',
          value: completedOrders.length.toString(),
          change: '+15,3%', // Mock change for now
      changeType: 'positive',
      icon: 'CheckCircle',
      color: 'warning'
    }
  ];

      setAnalyticsData({
        metrics,
        dealershipData,
        monthlyRevenueData,
        topDealerships,
        topTechnicians
      });


    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError(err.message || 'Fehler beim Laden der Analytics-Daten');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or filters change
  useEffect(() => {
    loadAnalyticsData();
  }, [filters]);

  // Note: All mock data has been replaced with real data from the database

  const getSelectedPeriod = () => {
    const monthNames = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    const monthName = monthNames[parseInt(filters.selectedMonth) - 1] || '';
    const year = filters.selectedYear;
    
    return `${monthName} ${year}`;
  };

  const handleExport = () => {
    // Real export functionality using actual data
    const exportData = {
      period: getSelectedPeriod(),
      totalRevenue: analyticsData.metrics?.[0]?.value || '€ 0,00',
      dealerships: analyticsData.dealershipData?.length || 0,
      orders: analyticsData.metrics?.[3]?.value || '0',
      generatedAt: new Date()?.toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })
    };

    
    // In a real application, this would generate and download a PDF
    alert(`Analytics-Bericht wird generiert...\n\nZeitraum: ${exportData?.period}\nGesamtumsatz: ${exportData?.totalRevenue}\nAutohäuser: ${exportData?.dealerships}\nAufträge: ${exportData?.orders}`);
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <>
      <Helmet>
        <title>Umsatz Analytics - CarHouse Service Manager</title>
        <meta name="description" content="Comprehensive revenue analytics dashboard with interactive charts and business intelligence for automotive service management" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header userRole="admin" />
        {/* Hide sidebar on mobile */}
        <div className="hidden lg:block">
          <Sidebar 
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={handleToggleSidebar}
            userRole="admin"
          />
        </div>

        {/* Main Content */}
        <div className={`transition-all duration-200 ease-out ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        } pt-16`}>
          <div className="p-4 lg:p-6 space-y-6">
            {/* Breadcrumb */}
            <Breadcrumb />

            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Umsatz Analytics
                </h1>
                <p className="text-text-secondary mt-1">
                  Umfassende Finanzanalyse und Geschäftsintelligenz
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => window.location?.reload()}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Aktualisieren
                </Button>
                {/* <Button
                  variant="default"
                  onClick={handleExport}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export
                </Button> */}
              </div>
            </div>

            {/* Filter Panel */}
            <FilterPanel
              filters={filters}
              onFiltersChange={setFilters}
              onExport={handleExport}
              isCollapsed={filterCollapsed}
              onToggleCollapse={() => setFilterCollapsed(!filterCollapsed)}
            />

            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {loading ? (
                // Loading skeleton
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse">
                    <div className="h-4 bg-muted rounded mb-2"></div>
                    <div className="h-8 bg-muted rounded mb-2"></div>
                    <div className="h-3 bg-muted rounded w-20"></div>
                  </div>
                ))
              ) : error ? (
                // Error state
                <div className="col-span-full bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                  <Icon name="AlertCircle" size={24} className="text-red-500 mx-auto mb-2" />
                  <p className="text-red-700 font-medium">Fehler beim Laden der Daten</p>
                  <p className="text-red-600 text-sm">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadAnalyticsData}
                    className="mt-3"
                  >
                    Erneut versuchen
                  </Button>
                </div>
              ) : (
                // Real data
                analyticsData.metrics?.map((metric, index) => (
                <MetricCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  color={metric?.color}
                />
                ))
              )}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {loading ? (
                // Loading skeleton for charts
                <>
                  <div className="bg-card border border-border rounded-lg p-6 animate-pulse">
                    <div className="h-6 bg-muted rounded mb-4 w-48"></div>
                    <div className="h-64 bg-muted rounded"></div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6 animate-pulse">
                    <div className="h-6 bg-muted rounded mb-4 w-48"></div>
                    <div className="h-64 bg-muted rounded"></div>
                  </div>
                </>
              ) : error ? null : (
                <>
                  <OrdersPerDealershipChart
                    data={analyticsData.dealershipData}
                selectedPeriod={getSelectedPeriod()}
              />
                                     <MonthlyRevenueChart
                     data={analyticsData.monthlyRevenueData}
                     selectedPeriod={filters.selectedYear}
                showNet={filters?.priceType === 'net'}
              />
                </>
              )}
            </div>

            {/* Top Performers */}
            {/* {loading ? (
              <div className="bg-card border border-border rounded-lg p-6 animate-pulse">
                <div className="h-6 bg-muted rounded mb-4 w-48"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-48 bg-muted rounded"></div>
                  <div className="h-48 bg-muted rounded"></div>
                </div>
              </div>
            ) : error ? null : (
            <TopPerformersCard
                dealerships={analyticsData.topDealerships}
                technicians={analyticsData.topTechnicians}
            />
            )} */}

            {/* Additional Insights */}
            {loading ? (
              <div className="bg-card border border-border rounded-lg p-6 animate-pulse">
                <div className="h-6 bg-muted rounded mb-4 w-48"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="p-4 bg-muted rounded-lg">
                      <div className="h-4 bg-muted-foreground rounded mb-2 w-24"></div>
                      <div className="h-3 bg-muted-foreground rounded w-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? null : (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                <Icon name="TrendingUp" size={20} className="mr-2" />
                Wichtige Erkenntnisse
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="ArrowUp" size={16} className="text-success" />
                      <span className="font-medium text-text-primary">Gesamtumsatz</span>
                  </div>
                  <p className="text-sm text-text-secondary">
                      {analyticsData.metrics?.[0]?.value || '€ 0,00'} im ausgewählten Zeitraum
                  </p>
                </div>
                
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Star" size={16} className="text-warning" />
                      <span className="font-medium text-text-primary">Top Autohaus</span>
                  </div>
                  <p className="text-sm text-text-secondary">
                      {analyticsData.topDealerships?.[0]?.name || 'Keine Daten'} mit {analyticsData.topDealerships?.[0]?.orders || 0} Aufträgen
                  </p>
                </div>
                
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                      <Icon name="CheckCircle" size={16} className="text-accent" />
                      <span className="font-medium text-text-primary">Abgeschlossene Aufträge</span>
                  </div>
                  <p className="text-sm text-text-secondary">
                      {analyticsData.metrics?.[3]?.value || '0'} Aufträge erfolgreich abgeschlossen
                  </p>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Quick Action Button for Mobile */}
        <QuickActionButton userRole="admin" />
      </div>
    </>
  );
};

export default RevenueAnalyticsDashboard;